'use client';
import React from 'react';

import Bookmaker from '@src/components/Icons/Bookmaker';

import CardMetric from '@src/components/Shared/CardMetric';
import FractionDisplay from '@src/components/Shared/FractionDisplay';
import NextLink from '@src/components/Shared/NextLink';

import useIsMobile from '@src/hooks/useIsMobile';
import useDebounce from '@src/hooks/useDebounce';

import ForceInput from '../ForceInput/ForceInput';
import UnitDropdown from '../UnitDropdown/UnitDropdown';
import { UnitTypes } from '../UnitDropdown/UnitDropdown.types';

import { EccentricUtilizationRatioCardPropTypes } from './EccentricUtilizationRatioCard.types';

const units: UnitTypes[] = [
  { id: 'centimeters', name: 'Centimeters', unit: 'cm' },
  { id: 'meters', name: 'Meters', unit: 'm' },
  { id: 'inches', name: 'Inches', unit: 'in' },
];

const EccentricUtilizationRatioCard: React.FC<EccentricUtilizationRatioCardPropTypes> = () => {
  const isMobile = useIsMobile();
  const [unit, setUnit] = React.useState<string>(units[0].id);

  // Main values used for calculation
  const [cmjValue, setCmjValue] = React.useState('');
  const [sjValue, setSjValue] = React.useState('');

  // Temporary values for mobile
  const [tempCmjValue, setTempCmjValue] = React.useState('');
  const [tempSjValue, setTempSjValue] = React.useState('');

  const handleCalculate = () => {
    // Only update the main values when Calculate is clicked (mobile)
    setCmjValue(tempCmjValue);
    setSjValue(tempSjValue);
  };

  const convertToCentimeters = (value: string, fromUnit: string) => {
    const v = parseFloat(value);
    if (isNaN(v)) return 0;
    switch (fromUnit) {
      case 'meters':
        return v * 100;
      case 'inches':
        return v * 2.54;
      default:
        return v; // centimeters
    }
  };

  // Always use main values for calculation
  const cmjCm = convertToCentimeters(cmjValue, unit);
  const sjCm = convertToCentimeters(sjValue, unit);

  let eur = 0;
  if (sjCm !== 0) eur = cmjCm / sjCm;
  const formattedEUR = eur.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  // --- Debounce the formattedEUR by 3000 ms ---
  const debouncedEUR = useDebounce(formattedEUR, 3000);

  // Fire a tracking event whenever the debounced EUR settles to a valid number
  React.useEffect(() => {
    // Only track if both inputs are non‐empty and debouncedEUR is a finite number
    if (
      cmjValue !== '' &&
      sjValue !== '' &&
      !isNaN(Number(debouncedEUR)) &&
      isFinite(Number(debouncedEUR))
    ) {
      if (typeof window !== 'undefined' && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: 'msk_calculator_calculation_performed',
          eventCategory: 'msk_calculator_usage',
          eventLabel: 'msk_calculator_eccentric_utilization_ratio',
          value: 1,
        });
      }
    }
    // Only re-run when debouncedEUR changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedEUR]);

  return (
    <CardMetric
      id='calculator-eccentric-utilization-ratio'
      className='mt-[2.4rem] lg:mt-[4rem] xl:mt-[unset]'
      title='EUR'
      productIcons={[
        {
          alt: 'ForceDecks App Icon',
          url: 'https://images.ctfassets.net/98s79sqwuajy/1sZL8SrOEPwzDxDhnJCoy2/d3561b92560bdd3fe2ecd709b6f4604f/forcedecks-app-icon.svg',
        },
      ]}
    >
      <p className='mb-[2.2rem] text-[1.2rem]'>
        The <strong className='font-medium'>Eccentric Utilization Ratio</strong> compares jump
        performance from the countermovement jump and squat jump to assess efficiency of the
        stretch-shortening cycle.
      </p>

      <FractionDisplay
        className='mb-[2rem]'
        label='Eccentric Utilization Ratio'
        numerator='<strong className="font-medium">Countermovement Jump</strong> Jump Height'
        denominator='<strong className="font-medium">Squat Jump</strong> Jump Height'
      />

      {/* Shared Unit Dropdown */}
      <UnitDropdown units={units} activeUnitId={unit} onChange={setUnit} />

      {/* Inputs */}
      <ForceInput
        className='mb-[1.4rem]'
        labelLines={['Countermovement Jump:', 'Jump Height']}
        units={units}
        activeUnit={unit}
        value={isMobile ? tempCmjValue : cmjValue}
        onChange={(e) => {
          if (isMobile) {
            setTempCmjValue(e.target.value); // Store temporarily on mobile
          } else {
            setCmjValue(e.target.value); // Update directly on desktop
          }
        }}
      />
      <ForceInput
        className='mb-[1.4rem]'
        labelLines={['Squat Jump:', 'Jump Height']}
        units={units}
        activeUnit={unit}
        value={isMobile ? tempSjValue : sjValue}
        onChange={(e) => {
          if (isMobile) {
            setTempSjValue(e.target.value); // Store temporarily on mobile
          } else {
            setSjValue(e.target.value); // Update directly on desktop
          }
        }}
      />

      {/* EUR Result with unit */}
      <div className='mb-[3.1rem] flex items-center justify-between font-normal text-primary'>
        <span className='ml-auto text-[1.4rem] font-medium'>Eccentric Utilization Ratio</span>
        <span className='ml-[2rem] border-l-[0.2rem] border-[#D9D9D9] pl-[2rem] text-[1.8rem] sm:text-[3rem]'>
          {isNaN(eur) || !isFinite(eur) ? '0.0' : formattedEUR}
        </span>
      </div>

      <div className='relative flex items-center gap-[1rem]'>
        <div className='absolute inset-x-[-9999px] top-[-2rem] h-[20rem] bg-[#F8F8F8]' />

        <div className='flex w-full items-center gap-[1.6rem] text-[1.2rem]'>
          <Bookmaker className='relative z-10 hidden h-[2rem] w-[1.6rem] max-w-[1.6rem] shrink-0 text-primary md:block' />

          <p className='text-pretty'>
            <NextLink
              className='font-light underline'
              href="https://www.globalperformanceinsights.com/post/the-eccentric-utilization-ratio-eur-explained#:~:text=The%20EUR%20is%20calculated%20by,plates%20like%20VALD%20Performance's%20ForceDecks."
            >
              Learn more
            </NextLink>{' '}
            about assessing and applying the EUR from Jo Clubb and Global Performance Insights.
          </p>
        </div>

        {isMobile && (
          <button
            onClick={handleCalculate}
            className='common-btn-transition right-0 bottom-0 relative ml-auto inline-flex h-[3.7rem] max-h-[3.7rem] items-center justify-center rounded-[0.6rem] border-primary bg-primary px-[2rem] py-[0.8rem] text-[1.6rem] font-normal leading-[1.6rem] tracking-[-0.0304rem] text-[#fff]! hover:border-primary--v2 hover:bg-primary--v2 hover:text-primary--v2'
            disabled={tempCmjValue === cmjValue && tempSjValue === sjValue}
          >
            Calculate
          </button>
        )}
      </div>
    </CardMetric>
  );
};

export default EccentricUtilizationRatioCard;
